import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  QrCode, UserCheck, Users, Clock, ArrowLeft, CheckCircle,
  WarningCircle, Hourglass, XCircle, ListChecks, Plus,
  ArrowsClockwise, Prohibit, UserPlus
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  checked_in: { color: '#10B981', icon: CheckCircle, label: 'Checked In' },
  expected: { color: '#3B82F6', icon: Clock, label: 'Expected' },
  no_show: { color: '#EF4444', icon: XCircle, label: 'No Show' },
  waitlisted: { color: '#F59E0B', icon: Hourglass, label: 'Waitlisted' },
  cancelled: { color: '#555', icon: Prohibit, label: 'Cancelled' },
};

export default function CheckInPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const targetType = params.get('type') || 'session';
  const targetId = params.get('id') || '';

  const [tab, setTab] = useState('attendees');
  const [attendees, setAttendees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrToken, setQrToken] = useState('');

  const fetchData = async () => {
    if (!targetId) { setLoading(false); return; }
    try {
      const [aRes, sRes, tRes] = await Promise.all([
        axios.get(`${API}/api/attendance/list/${targetType}/${targetId}`, { withCredentials: true }),
        axios.get(`${API}/api/attendance/summary/${targetType}/${targetId}`, { withCredentials: true }),
        axios.get(`${API}/api/attendance/tokens/${targetType}/${targetId}`, { withCredentials: true }),
      ]);
      setAttendees(aRes.data);
      setSummary(sRes.data);
      setTokens(tRes.data);
      const activeToken = tRes.data.find(t => t.token_status === 'active');
      if (activeToken) setQrToken(activeToken.token);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); const iv = setInterval(fetchData, 10000); return () => clearInterval(iv); }, [targetType, targetId]);

  const generateToken = async () => {
    try {
      const r = await axios.post(`${API}/api/attendance/tokens`, {
        attendance_target_type: targetType, attendance_target_id: targetId, expires_hours: 24,
      }, { withCredentials: true });
      setQrToken(r.data.token);
      toast.success('QR code generated');
      fetchData();
    } catch (e) { toast.error('Failed'); }
  };

  const revokeToken = async (tokenId) => {
    try {
      await axios.post(`${API}/api/attendance/tokens/${tokenId}/revoke`, {}, { withCredentials: true });
      toast.success('Token revoked');
      fetchData();
    } catch (e) { toast.error('Failed'); }
  };

  const rotateToken = async (tokenId) => {
    try {
      const r = await axios.post(`${API}/api/attendance/tokens/${tokenId}/rotate`, {}, { withCredentials: true });
      setQrToken(r.data.token);
      toast.success('Token rotated');
      fetchData();
    } catch (e) { toast.error('Failed'); }
  };

  const staffCheckIn = async (userId) => {
    try {
      await axios.post(`${API}/api/attendance/staff-check-in/${targetType}/${targetId}`, { user_id: userId }, { withCredentials: true });
      toast.success('Checked in');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const markNoShow = async (userId) => {
    try {
      await axios.post(`${API}/api/attendance/mark-no-show/${targetType}/${targetId}`, { user_id: userId }, { withCredentials: true });
      toast.success('Marked no-show');
      fetchData();
    } catch (e) { toast.error('Failed'); }
  };

  const promoteWaitlist = async (userId) => {
    try {
      await axios.post(`${API}/api/attendance/waitlist/${targetType}/${targetId}/promote`, { user_id: userId }, { withCredentials: true });
      toast.success('Promoted from waitlist');
      fetchData();
    } catch (e) { toast.error('Failed'); }
  };

  const selfCheckIn = async () => {
    try {
      await axios.post(`${API}/api/attendance/check-in`, {
        attendance_target_type: targetType, attendance_target_id: targetId,
      }, { withCredentials: true });
      toast.success('Checked in!');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (!targetId) return (
    <div className="max-w-3xl mx-auto text-center py-12">
      <QrCode size={40} className="mx-auto mb-3 text-[#333]" />
      <p className="text-[#555]">No event selected. Navigate to an event and use check-in from there.</p>
    </div>
  );

  if (loading) return <div className="max-w-3xl mx-auto"><div className="h-40 bg-white/[0.03] rounded-lg animate-pulse" /></div>;

  const qrValue = qrToken ? `${window.location.origin}/check-in/scan?token=${qrToken}` : '';

  return (
    <div className="max-w-3xl mx-auto" data-testid="check-in-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[12px] text-[#555] hover:text-white mb-4 transition-colors">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Event Check-In</h1>
          <p className="text-[13px] text-[#444] mt-0.5">{targetType.replace(/_/g, ' ')} | {targetId.slice(0, 8)}...</p>
        </div>
        <Button size="sm" onClick={selfCheckIn} className="h-8 text-[11px] bg-[#10B981] hover:bg-[#059669] text-white" data-testid="self-check-in-btn">
          <UserCheck size={13} className="mr-1" /> Check In
        </Button>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-5 gap-2 mb-5">
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <div key={key} className="bg-[#0c0c14] border border-white/[0.06] rounded-lg p-3 text-center">
              <cfg.icon size={16} weight="duotone" style={{ color: cfg.color }} className="mx-auto mb-1" />
              <p className="text-lg font-bold text-white">{summary[key] || 0}</p>
              <p className="text-[9px] text-[#555] uppercase tracking-wider">{cfg.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-[#0c0c14] rounded-lg p-1 w-fit border border-white/[0.06]">
        {[
          { id: 'attendees', label: 'Attendees', icon: ListChecks },
          { id: 'qr', label: 'QR Code', icon: QrCode },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[12px] font-medium transition-colors ${
              tab === t.id ? 'bg-[#10B981]/15 text-[#10B981]' : 'text-[#555] hover:text-[#999]'
            }`} data-testid={`tab-${t.id}`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {/* Attendees Tab */}
      {tab === 'attendees' && (
        <div className="space-y-2">
          {attendees.length === 0 ? (
            <div className="text-center py-8 text-[#444]">
              <Users size={28} className="mx-auto mb-2 opacity-30" />
              <p className="text-[13px]">No attendees yet</p>
            </div>
          ) : attendees.map(a => {
            const cfg = STATUS_CONFIG[a.attendance_status] || STATUS_CONFIG.expected;
            return (
              <div key={a.id} className="flex items-center gap-3 bg-[#0c0c14] border border-white/[0.06] rounded-lg px-4 py-3"
                data-testid={`attendee-${a.user_id}`}>
                <div className="w-8 h-8 rounded-full bg-[#10B981]/10 flex items-center justify-center text-[10px] font-bold" style={{ color: cfg.color }}>
                  {a.user_name?.charAt(0) || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] text-white font-medium truncate">{a.user_name || a.user_id.slice(0, 8)}</p>
                  <p className="text-[10px] text-[#555]">{a.source_type?.replace(/_/g, ' ') || 'pending'} {a.checked_in_at ? `| ${new Date(a.checked_in_at).toLocaleTimeString()}` : ''}</p>
                </div>
                <span className="text-[9px] uppercase px-2 py-0.5 rounded-full font-medium" style={{ color: cfg.color, background: `${cfg.color}10`, border: `1px solid ${cfg.color}30` }}>
                  {cfg.label}
                </span>
                {a.attendance_status === 'expected' && (
                  <div className="flex gap-1">
                    <button onClick={() => staffCheckIn(a.user_id)} className="px-2 py-1 text-[9px] rounded bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20 hover:bg-[#10B981]/20" data-testid={`staff-checkin-${a.user_id}`}>Check In</button>
                    <button onClick={() => markNoShow(a.user_id)} className="px-2 py-1 text-[9px] rounded bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20 hover:bg-[#EF4444]/20">No Show</button>
                  </div>
                )}
                {a.attendance_status === 'waitlisted' && (
                  <button onClick={() => promoteWaitlist(a.user_id)} className="px-2 py-1 text-[9px] rounded bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20 hover:bg-[#F59E0B]/20" data-testid={`promote-${a.user_id}`}>Promote</button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* QR Code Tab */}
      {tab === 'qr' && (
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-6 text-center">
          {qrToken ? (
            <>
              <h3 className="text-[14px] font-bold text-white mb-4">Scan to Check In</h3>
              <div className="inline-block p-4 bg-white rounded-xl mb-4" data-testid="qr-code-display">
                <QRCodeSVG value={qrValue} size={200} level="H" />
              </div>
              <p className="text-[11px] text-[#555] mb-4">Players scan this code at the venue to check in</p>
              <div className="flex gap-2 justify-center">
                {tokens.filter(t => t.token_status === 'active').map(t => (
                  <div key={t.id} className="flex gap-1">
                    <button onClick={() => rotateToken(t.id)}
                      className="px-3 py-1.5 text-[10px] rounded bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/20 hover:bg-[#3B82F6]/20 flex items-center gap-1"
                      data-testid="rotate-token-btn">
                      <ArrowsClockwise size={12} /> Rotate
                    </button>
                    <button onClick={() => revokeToken(t.id)}
                      className="px-3 py-1.5 text-[10px] rounded bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20 hover:bg-[#EF4444]/20 flex items-center gap-1"
                      data-testid="revoke-token-btn">
                      <Prohibit size={12} /> Revoke
                    </button>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <QrCode size={48} className="mx-auto mb-3 text-[#333]" />
              <p className="text-[13px] text-[#555] mb-4">Generate a QR code for event check-in</p>
              <Button onClick={generateToken} className="bg-[#10B981] hover:bg-[#059669] text-white text-[12px]" data-testid="generate-qr-btn">
                <Plus size={14} className="mr-1" /> Generate QR Code
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
